import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { User } from '@/types';
import { Button } from '@/components/ui/Button';
import { LogOut, User as UserIcon, Lock, Bell, Shield, CreditCard, Users, HelpCircle, FileText } from 'lucide-react';

interface Section {
  key: string;
  label: string;
  icon: React.ReactNode;
  component: React.ReactNode;
}

interface SettingsLayoutProps {
  user: User;
  sections: Section[];
}

export function SettingsLayout({ user, sections }: SettingsLayoutProps) {
  const [selected, setSelected] = useState(sections[0]?.key || '');

  return (
    <div className="flex flex-col md:flex-row gap-8 min-h-[70vh]">
      {/* Responsive nav: horizontal scroll on mobile, sidebar on desktop */}
      <nav
        className={
          cn(
            'w-full md:w-64 flex md:flex-col flex-row gap-2 md:gap-1 rounded-lg p-2 md:p-0',
            'overflow-x-auto md:overflow-visible',
            'bg-dark-200 md:bg-transparent',
            'sticky top-16 z-10 md:static md:z-auto'
          )
        }
        aria-label="Settings sections"
      >
        {sections.map((section) => (
          <button
            key={section.key}
            className={cn(
              'flex items-center gap-2 md:gap-3 px-4 py-2 md:py-3 rounded-lg font-medium transition-colors whitespace-nowrap',
              selected === section.key
                ? 'bg-primary-700 text-sand-100 shadow'
                : 'hover:bg-dark-300 text-sand-300',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              'relative'
            )}
            aria-current={selected === section.key}
            aria-label={section.label}
            onClick={() => setSelected(section.key)}
          >
            <span className="w-5 h-5 flex items-center justify-center">{section.icon}</span>
            <span className="inline text-base font-semibold">{section.label}</span>
            {/* Visual indicator for mobile */}
            {selected === section.key && (
              <span className="absolute left-2 right-2 -bottom-1 h-1 rounded bg-primary-500 md:hidden" />
            )}
          </button>
        ))}
      </nav>
      <section className="flex-1 w-full max-w-2xl mx-auto md:mx-0 bg-dark-200 rounded-lg p-6 shadow-lg">
        {sections.find((s) => s.key === selected)?.component}
      </section>
    </div>
  );
}

// Example usage (to be replaced in Settings.tsx):
// <SettingsLayout
//   user={user}
//   sections={[
//     { key: 'account', label: 'Account', icon: <UserIcon />, component: <AccountSettings user={user} /> },
//     { key: 'profile', label: 'Profile', icon: <UserIcon />, component: <ProfileSettings user={user} /> },
//     { key: 'security', label: 'Security', icon: <Lock />, component: <SecuritySettings /> },
//     { key: 'notifications', label: 'Notifications', icon: <Bell />, component: <NotificationSettings /> },
//     // ...
//   ]}
// /> 